/**
 * Where a gardener can be found.
 *
 * Seven optional boxes (website, Instagram, Facebook, Pinterest, YouTube,
 * TikTok, X), kept apart from the address form because they answer a
 * different question: that one is where the garden is, for the forecast, and
 * a mistake in it plans the wrong year. These are only ever shown to people.
 *
 * Every box takes the bare handle (E7, owner 2026-10-26: "consistency is the
 * key here"). The server cleans what is typed (any @, http, www., the
 * platform's address and any tail come off), checks it against the
 * platform's rules when Save is pressed, and builds the address: the screen
 * draws what the server sends and decides nothing. One hint under each label
 * says what to type, in the platform's own word ("username", "handle"); after
 * a save each box shows the cleaned value the server kept; a box the server
 * refused, or a saved value that fails the rules, shows the server's reason
 * under it, in the reader's language.
 *
 * A screen of its own with one Save at the foot, the same as the name and
 * address: what is typed on the profile is saved with a button, what is
 * tapped is kept at once.
 *
 * Used by: UserProfile (?s=links) and the share studio's "Your handle" chip when
 * there is no handle yet (B85), which passes onSaved and is told the links the
 * server kept. The share frames and the public pages read the server's
 * profile_links (shared/lib/socialLinks linksOf) to print the handles.
 *
 * Words: profile.links.* (title, description, the places' names, hint.*,
 * placeholder, placeholderWebsite), profile.saveFailed.
 *
 * @module ProfileLinksCard
 */

import React from 'react';
import { SocialIcon } from '@/shared/components/SocialIcon';
import type { LinkKey, ProfileLink } from '@/shared/lib/socialLinks';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { updateUserProfile } from '@/shared/api/users';
import { debugLog } from '@/config/config';
import { errorMessage, refusalFields } from '@/shared/lib/errorMessage';
import { cn } from '@/lib/utils';
import { ProfileScreen } from './ProfileScreen';
import { SaveFoot } from './profileKit';

/** Every box's text, by place. */
type Links = Partial<Record<LinkKey, string>>;

/**
 * Each box's text as the server read the account: the cleaned value, or a saved value that fails, as saved.
 *
 * @param links - The account's profile_links.
 * @returns The boxes' text, by place.
 */
const valuesOf = (links: ReadonlyArray<ProfileLink>): Links => Object.fromEntries(links.map((link) => [link.key, link.value]));

/**
 * Profile > Links: a box per place, saved together with one button.
 *
 * @param props.open - Whether the screen is showing.
 * @param props.onClose - Called when the gardener leaves it.
 * @param props.onSaved - Called after a save worked, with the links the server kept (the share studio closes it and,
 *   when a handle was saved, turns it on and says where to change it).
 */
export const ProfileLinksScreen: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Called once the links are saved, with the server's profile_links, for a caller that closes the screen and uses them (the share studio). */
  onSaved?: (links: ProfileLink[]) => void;
}> = ({ open, onClose, onSaved }) => {
  const { mysqlUser, updateMysqlUser } = useAuth();
  const { reactTranslate: t } = useTranslation();
  /** The boxes as the server last read them: their order, their values and a saved value's problem. */
  const [saved, setSaved] = React.useState<ProfileLink[]>(() => mysqlUser?.profile_links ?? []);
  /** Whether a save is in flight. */
  const [saving, setSaving] = React.useState(false);
  /** The words for a failed save, or null. */
  const [error, setError] = React.useState<string | null>(null);
  /** The server's reason for each box the last save refused. */
  const [refused, setRefused] = React.useState<Partial<Record<LinkKey, string>>>({});
  /** Whether the last change was saved, for the footer. */
  const [justSaved, setJustSaved] = React.useState(false);
  /** What is typed in each box. */
  const [values, setValues] = React.useState<Links>(() => valuesOf(saved));

  // Filled as the screen opens, and only then, so nothing saved elsewhere
  // undoes what is being typed.
  React.useEffect(() => {
    if (!open) return;
    const now = mysqlUser?.profile_links ?? [];
    setSaved(now);
    setValues(valuesOf(now));
    setRefused({});
    setError(null);
    setJustSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** What is saved on the account, box by box. */
  const kept = valuesOf(saved);
  /** Whether any box differs from what is saved. */
  const dirty = saved.some((link) => (values[link.key] ?? '').trim() !== (kept[link.key] ?? '').trim());

  /** Saves every box; true when kept, so leaving with changes can close after. */
  const save = async (): Promise<boolean> => {
    if (!mysqlUser) return false;
    setSaving(true);
    setError(null);
    setRefused({});
    try {
      const body = Object.fromEntries(saved.map((link) => [link.key, values[link.key] ?? '']));
      const updated = await updateUserProfile(mysqlUser.user_id, body);
      updateMysqlUser(updated);
      // The boxes show what the server kept: the bare handle, a website's address.
      setSaved(updated.profile_links);
      setValues(valuesOf(updated.profile_links));
      setJustSaved(true);
      onSaved?.(updated.profile_links);
      return true;
    } catch (err) {
      debugLog('UserProfile', 'Could not save links', err);
      // A refusal names each box it refused, in the reader's language, and
      // its first reason is the foot's; anything else is the server's refusal
      // where it gave one, or the standard sentence (errorMessage).
      setRefused(refusalFields(err) ?? {});
      setError(errorMessage(err, t('profile.saveFailed')));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileScreen
      open={open}
      title={t('profile.links.title')}
      onClose={onClose}
      dirty={dirty}
      onSave={save}
      onDiscard={() => { setValues(kept); setRefused({}); }}
      footer={<SaveFoot dirty={dirty} saving={saving} error={error} justSaved={justSaved} onSave={() => { void save(); }} />}
    >
      <p className="text-sm text-muted-foreground">{t('profile.links.description')}</p>
      {/* Each place wears its own mark in its own colour, the same mark the
          share frame prints beside the handle, so what is typed here and what
          goes out on a photograph look like the same thing. */}
      {saved.map((link) => {
        const { key } = link;
        const value = values[key] ?? '';
        // The reason a save refused this box; else, while the box still holds the saved value, why that is not used.
        const reason = refused[key] ?? (value.trim() === (kept[key] ?? '').trim() ? link.problem : null);
        const hintId = `link-${key}-hint`;
        const reasonId = `link-${key}-reason`;
        return (
          <div key={key} className="space-y-1.5">
            <Label htmlFor={`link-${key}`} className="flex items-center gap-2 text-sm font-medium">
              <SocialIcon name={key} size={16} />
              {t(`profile.links.${key}`)}
            </Label>
            <p id={hintId} className="text-xs text-muted-foreground">{t(`profile.links.hint.${key}`)}</p>
            <Input
              id={`link-${key}`}
              value={value}
              onChange={(e) => {
                const typed = e.target.value;
                setValues((current) => ({ ...current, [key]: typed }));
                setRefused((current) => {
                  if (!current[key]) return current;
                  const { [key]: _gone, ...rest } = current;
                  return rest;
                });
              }}
              placeholder={key === 'website' ? t('profile.links.placeholderWebsite') : t('profile.links.placeholder')}
              inputMode={key === 'website' ? 'url' : 'text'}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={reason ? true : undefined}
              aria-describedby={reason ? `${hintId} ${reasonId}` : hintId}
              className={cn(reason && 'border-destructive')}
            />
            {reason && <p id={reasonId} className="text-sm text-destructive">{reason}</p>}
          </div>
        );
      })}
    </ProfileScreen>
  );
};
